// ─── Causar la factura del proveedor desde su XML ───────────────────────────
// En Facturas proveedor se suelta el XML (o el ZIP tal como llega al correo) y
// AURA hace lo que antes se tecleaba:
//   · elige el proveedor por su NIT (o ofrece crear la ficha con los datos de
//     la factura);
//   · pone el número y la fecha de la factura;
//   · busca en las OCP por causar de ese proveedor las líneas que la factura
//     trae y las marca con la cantidad y el precio facturados; lo que la
//     factura no trae queda desmarcado (pendiente en la orden);
//   · compara lo que se va a causar con lo que dice la factura (subtotal, IVA,
//     total) y avisa cualquier diferencia ANTES de causar;
//   · guarda el CUFE, y con él no deja causar dos veces la misma factura.
// La lectura del XML es src/core/facturaXML.js (puro, con pruebas).
import { cruzarLineas, leerArchivoFactura, nitBase } from "../core/facturaXML.js";
import { normNumeroFactura } from "../core/facturaIdentidad.js";
import { getParametrosFiscales, saveData } from "../core/cloudSync.js";
import { EMPRESA } from "../core/config.js";
import { state } from "../core/state.js";
import { cloudState, supa } from "../core/supabaseClient.js";
import { marcarCausadaEnBandeja } from "./bandejaFacturas.js";
import { escapeHTML, fmtCOP, fmtDate, fmtPrecio, genId, parseNumber, today } from "../core/utils.js";
import { render } from "../ui/app.js";
import { fprovInitOcp, ocpPorCausar } from "./cxp.js";

const diasEntre = (a, b) => {
  const d = (new Date(b + "T12:00:00") - new Date(a + "T12:00:00")) / 86400000;
  return Number.isFinite(d) && d > 0 ? Math.round(d) : 0;
};

export function proveedorDeFactura(fx) {
  const nit = nitBase(fx && fx.proveedor && fx.proveedor.nit);
  if (!nit) return null;
  return (state.data.proveedores || []).find(p => nitBase(p.nit) === nit) || null;
}

// ¿Esta factura ya se causó? Por el CUFE, o por el número dentro del mismo
// proveedor (una causada a mano antes del XML no trae CUFE).
export function causacionDeFactura(fx, provId) {
  const num = normNumeroFactura(fx.numero);
  for (const o of state.data.ocp || []) {
    for (const fc of o._facturasContables || []) {
      if ((fx.cufe && fc.cufe && fc.cufe === fx.cufe) ||
          (provId && o.proveedor_id === provId && num && normNumeroFactura(fc.factNum) === num)) {
        return { ocp: o, fc };
      }
    }
  }
  return null;
}

// Marca en las OCP del proveedor lo que la factura trae.
export function aplicarFacturaXML(fx) {
  const prov = proveedorDeFactura(fx);
  const fp = state.fprov = { provId: prov ? prov.id : "", numFact: fx.numero || "", fechaFact: fx.fecha || today(),
    ocps: {}, busqOcp: "", xml: fx, xmlCruce: [], provNuevo: !prov };
  if (!prov) return fp;
  (state.data.ocp || []).filter(o => o.proveedor_id === prov.id && ocpPorCausar(o)).forEach(o => fprovInitOcp(o.id));
  const candidatas = [];
  Object.entries(fp.ocps).forEach(([ocpId, s]) => {
    const ocp = (state.data.ocp || []).find(o => o.id === ocpId) || {};
    s.on = false;
    (s.items || []).forEach((it, idx) => {
      it.on = false;
      candidatas.push({ ocpId, idx, ocpNumero: ocp.numero, articulo: it.art, pendiente: parseNumber(it.cant) || 0, precio: parseNumber(it.pu) || 0 });
    });
  });
  fp.xmlCruce = cruzarLineas(fx.lineas, candidatas);
  fp.xmlCruce.forEach((m, i) => {
    if (!m) return;
    const s = fp.ocps[m.ocpId];
    const it = s.items[m.idx];
    const l = fx.lineas[i];
    s.on = true; it.on = true; it.cant = l.cantidad; it.pu = l.precioUnitario;
  });
  return fp;
}

// El archivo que la persona suelta o elige.
export async function fprovCargarArchivo(archivo) {
  if (!archivo) return;
  let fx;
  try { fx = await leerArchivoFactura(archivo.name, await archivo.arrayBuffer()); }
  catch (e) { alert("⚠️ No se pudo leer la factura: " + e.message); return; }
  await abrirFacturaLeida(fx);
}

// Una factura ya leída —del archivo soltado o de la bandeja del correo— entra
// a la causación por aquí: las mismas validaciones para las dos.
// `extra.recibida` = { id, pdf_url } cuando viene de la bandeja.
export async function abrirFacturaLeida(fx, extra = {}) {
  if (fx.tipo !== "factura") {
    alert(`⚠️ ${fx.numero} es una ${fx.tipo === "nota_credito" ? "nota crédito" : "nota débito"} del proveedor${fx.facturaReferida ? ` sobre la factura ${fx.facturaReferida.numero}` : ""}.\n\nPor ahora aquí solo se cargan facturas.`);
    return;
  }
  // Una factura a nombre de OTRA empresa no se causa aquí: el IVA no sería
  // descontable para esta y el gasto no es suyo.
  const nitEmpresa = nitBase(EMPRESA.nit);
  if (nitEmpresa && fx.adquirente && fx.adquirente.nit && nitBase(fx.adquirente.nit) !== nitEmpresa) {
    alert(`⚠️ Esta factura es para ${fx.adquirente.nombre || "otra empresa"} (NIT ${fx.adquirente.nit}), no para ${EMPRESA.nombre || "tu empresa"} (NIT ${EMPRESA.nit}).\n\nNo se cargó.`);
    return;
  }
  const prov = proveedorDeFactura(fx);
  const ya = causacionDeFactura(fx, prov && prov.id);
  if (ya) {
    // Si vino del correo, sale de la bandeja: ya está causada.
    marcarCausadaEnBandeja(fx.cufe, { ocps: [ya.ocp.numero], factura: fx.numero, antes: true });
    alert(`⚠️ La factura ${fx.numero} de ${fx.proveedor.nombre} ya se causó` +
      ` en la ${ya.ocp.numero} el ${fmtDate(ya.fc.fecha)} (${fmtCOP(ya.fc.total || 0)}).\n\nNo se puede causar dos veces.`);
    return;
  }
  if (fx.validadaDian === false) {
    if (!confirm(`⚠️ La DIAN NO validó la factura ${fx.numero} (${fx.validacion.descripcion || fx.validacion.codigo}).\n\nUna factura rechazada no da derecho a costo ni a IVA descontable. ¿Cargarla de todas formas?`)) return;
  }
  aplicarFacturaXML(fx);
  // El PDF guardado en OneDrive, para compararlo al lado. Si el XML se soltó a
  // mano pero la factura también llegó al correo, se busca por el CUFE.
  let recibida = extra.recibida || null;
  if (!recibida && fx.cufe && cloudState.tenantId) {
    try {
      const { data } = await supa.from("facturas_recibidas").select("id,pdf_url,pdf_ruta").eq("tenant_id", cloudState.tenantId).eq("cufe", fx.cufe).maybeSingle();
      if (data) recibida = data;
    } catch { /* sin bandeja, sin PDF: se causa igual */ }
  }
  state.fprov.recibida = recibida;
  state.page = "fact-prov";
  render();
}

// La ficha mínima con lo que trae la factura. Las tarifas de retención no
// vienen en el XML: se avisa que hay que completarlas.
export function crearProveedorDesdeXML() {
  const fp = state.fprov;
  const fx = fp && fp.xml;
  if (!fx || proveedorDeFactura(fx)) return;
  const p = fx.proveedor;
  const nuevo = {
    id: genId(), nombre: p.nombre, nit: p.nit, dv: p.dv || null,
    tipo_documento: p.tipoDoc === "13" ? "CC" : "NIT", tipo_persona: p.tipoDoc === "13" ? "natural" : "juridica",
    email: p.email || "", telefono: p.telefono || "", ciudad: p.ciudad || "", direccion: p.direccion || "",
    dias_pago: fx.formaPago === "contado" ? 0 : (diasEntre(fx.fecha, fx.vence) || 30),
    responsable_iva: (fx.totales.iva || 0) > 0
  };
  saveData("proveedores", [...(state.data.proveedores || []), nuevo]);
  aplicarFacturaXML(fx);
  render();
  alert(`✅ Proveedor ${nuevo.nombre} creado con los datos de la factura.\n\nRevisa en Maestros → Proveedores sus tarifas de retención: la factura no las trae.`);
}

// ¿Lo que se va a causar es lo que dice la factura? Diferencias de más de un
// peso, por concepto.
export function diferenciasConXML(fx, calc) {
  if (!fx) return [];
  const filas = [
    ["Subtotal", fx.totales.subtotal, calc.subtotal],
    ["IVA", fx.totales.iva, calc.iva],
    ["Total", fx.totales.total, calc.total]
  ];
  return filas.map(([concepto, factura, aura]) => ({ concepto, factura, aura, cuadra: Math.abs(factura - aura) <= 1 }));
}

export function panelXmlFprov(fp, calc) {
  const fx = fp && fp.xml;
  if (!fx) return "";
  const p = fx.proveedor || {};
  const prov = proveedorDeFactura(fx);
  const dif = diferenciasConXML(fx, { subtotal: calc.subtotal ?? calc.sub ?? 0, iva: calc.iva || 0, total: calc.total || 0 });
  const todoCuadra = dif.every(d => d.cuadra);
  const sinCruce = (fp.xmlCruce || []).filter(m => !m).length;
  const lineas = fx.lineas.map((l, i) => {
    const m = (fp.xmlCruce || [])[i];
    const pend = m ? m.pendiente : 0;
    return `<tr data-xml-linea="${i}" data-xml-cruce="${m ? "si" : "no"}" style="border-top:1px solid #e0f2fe;">
      <td style="padding:4px 6px;font-size:10.5px;">${escapeHTML(l.descripcion || "—")}</td>
      <td style="padding:4px 6px;font-size:10.5px;text-align:right;white-space:nowrap;">${l.cantidad} × ${fmtPrecio(l.precioUnitario)}<div style="color:#64748b;font-size:9.5px;">= ${fmtPrecio(l.subtotal)}</div></td>
      <td style="padding:4px 6px;font-size:10.5px;text-align:right;">${l.ivaPct ? l.ivaPct + "%" : "sin IVA"}</td>
      <td style="padding:4px 6px;font-size:10.5px;">${m
        ? `<span style="color:#047857;font-weight:700;">✅ ${escapeHTML(m.ocpNumero || "")} · ${escapeHTML(m.articulo)}</span>${l.cantidad > pend + 0.001 ? `<div style="color:#b45309;font-size:9.5px;font-weight:700;">⚠️ factura ${l.cantidad}, la orden tiene pendiente ${pend}</div>` : ""}`
        : `<span style="color:#b45309;font-weight:700;">⚠️ Ninguna OCP pendiente la respalda</span>`}</td>
    </tr>`;
  }).join("");
  return `
    <div data-fprov-xml style="background:#f0f9ff;border:1.5px solid #7dd3fc;border-radius:8px;padding:10px 12px;margin-bottom:12px;">
      <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;flex-wrap:wrap;">
        <div>
          <div style="font-size:12.5px;font-weight:800;color:#0369a1;">📄 Factura electrónica ${escapeHTML(fx.numero)} · ${escapeHTML(p.nombre || "")}</div>
          <div style="font-size:10.5px;color:#475569;margin-top:2px;">
            NIT ${escapeHTML(p.nit || "")}${p.dv ? "-" + escapeHTML(p.dv) : ""} · ${fmtDate(fx.fecha)}${fx.vence ? ` · vence ${fmtDate(fx.vence)}` : ""} · ${fx.formaPago === "contado" ? "contado" : "crédito"}
            ${fx.validadaDian === true ? ` · <b style="color:#047857;">✅ validada por la DIAN</b>` : fx.validadaDian === false ? ` · <b style="color:#dc2626;">⛔ NO validada por la DIAN</b>` : ""}
          </div>
          <div style="font-size:9.5px;color:#94a3b8;margin-top:2px;word-break:break-all;" title="${escapeHTML(fx.cufe)}">CUFE ${escapeHTML((fx.cufe || "").slice(0, 24))}…</div>
        </div>
        <div style="display:flex;gap:6px;align-items:center;">
          ${fp.recibida && fp.recibida.pdf_url ? `<a data-fprov-ver-pdf href="${escapeHTML(fp.recibida.pdf_url)}" target="_blank" rel="noopener"
            style="background:#0369a1;color:white;padding:5px 12px;border-radius:6px;font-size:10.5px;font-weight:700;text-decoration:none;" title="El PDF de la factura, guardado en OneDrive: compáralo con lo que vas a causar">📄 Ver PDF de la factura</a>` : ""}
          <button onclick="fprovReset();render();" style="background:white;border:1px solid #cbd5e1;padding:4px 10px;border-radius:6px;font-size:10px;font-weight:700;cursor:pointer;color:#475569;">✕ Quitar XML</button>
        </div>
      </div>
      ${!prov ? `
        <div data-fprov-xml-nuevo style="margin-top:8px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;padding:8px 10px;font-size:11px;color:#92400e;">
          <b>${escapeHTML(p.nombre || "Este proveedor")}</b> (NIT ${escapeHTML(p.nit || "")}) no está en Maestros.
          <button data-action="fprov-crear-proveedor-xml" style="margin-left:8px;background:#0E7490;color:white;border:none;padding:5px 12px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;">➕ Crear proveedor con los datos de la factura</button>
        </div>` : `
      <table style="width:100%;border-collapse:collapse;margin-top:8px;background:white;border-radius:6px;">
        <thead><tr style="background:#e0f2fe;">
          <th style="padding:4px 6px;text-align:left;font-size:9px;color:#0369a1;text-transform:uppercase;">En la factura</th>
          <th style="padding:4px 6px;text-align:right;font-size:9px;color:#0369a1;text-transform:uppercase;">Cant. × precio</th>
          <th style="padding:4px 6px;text-align:right;font-size:9px;color:#0369a1;text-transform:uppercase;">IVA</th>
          <th style="padding:4px 6px;text-align:left;font-size:9px;color:#0369a1;text-transform:uppercase;">Se cruza con</th>
        </tr></thead>
        <tbody>${lineas}</tbody>
      </table>
      ${sinCruce && sinCruce === fx.lineas.length ? `
      <div data-fprov-xml-sin-ocp style="margin-top:8px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;padding:8px 10px;font-size:11px;color:#92400e;line-height:1.5;">
        <b>⚠️ Esta factura no tiene orden de compra en AURA.</b> Ninguna OCP pendiente de ${escapeHTML(prov.nombre || "este proveedor")} trae ${fx.lineas.length === 1 ? "este artículo" : "estos artículos"}, así que aquí no hay nada que causar.
        <div style="margin-top:4px;">· Si es mercancía que se compró sin orden: crea la OCP (Compras → Órdenes de compra), ingrésala y vuelve a cargar este XML.</div>
        <div>· Si es un gasto (servicio, arriendo, muestra): se causará como gasto sin orden de compra, que está en construcción.</div>
      </div>` : `
      ${sinCruce ? `<div style="font-size:10.5px;color:#b45309;margin-top:6px;">⚠️ ${sinCruce} línea${sinCruce === 1 ? "" : "s"} de la factura sin orden de compra: no se ${sinCruce === 1 ? "causa" : "causan"} aquí, y por eso el cuadre de abajo no da. Revisa si falta la OCP o si es un gasto.</div>` : ""}
      <div data-fprov-xml-cuadre="${todoCuadra ? "si" : "no"}" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;">
        ${dif.map(d => `<div style="flex:1;min-width:150px;background:${d.cuadra ? "#ecfdf5" : "#fef2f2"};border:1px solid ${d.cuadra ? "#a7f3d0" : "#fecaca"};border-radius:6px;padding:5px 8px;font-size:10.5px;">
          <div style="font-weight:700;color:${d.cuadra ? "#047857" : "#b91c1c"};">${d.cuadra ? "✅" : "⚠️"} ${d.concepto}</div>
          <div style="color:#475569;">Factura ${fmtPrecio(d.factura)}${d.cuadra ? (Math.abs(d.factura - d.aura) >= 0.005 ? ` · AURA ${fmtCOP(d.aura)}` : "") : ` · se causaría ${fmtCOP(d.aura)}`}</div>
        </div>`).join("")}
      </div>
      ${dif.some(d => d.cuadra && Math.abs(d.factura - d.aura) >= 0.005) && todoCuadra ? `<div data-fprov-xml-centavos style="font-size:10px;color:#475569;margin-top:6px;">ℹ️ La factura trae centavos y AURA lleva la contabilidad en pesos enteros: la diferencia es solo de redondeo y el total a pagar es el mismo.</div>` : ""}
      ${(fx.totales.iva || 0) > 0 && !prov.responsable_iva ? `<div data-fprov-xml-ficha-iva style="font-size:10.5px;color:#b91c1c;margin-top:6px;">⚠️ El proveedor cobra IVA y su ficha dice que no es responsable de IVA: corrígela en Maestros → Proveedores para que el IVA se tome como descontable.</div>` : ""}`}`}
    </div>`;
}

// ─── La retención que la ficha no deja practicar ────────────────────────────
// La retención en la fuente NO viene en la factura del proveedor: la practica
// quien paga (Mathiz, persona jurídica, es agente de retención — art. 368
// E.T.), y AURA la calcula con la tarifa de la FICHA del tercero
// (calcularImpuestosCausacion). Una ficha sin tarifa no retiene nada, en
// silencio, aunque la compra supere la base mínima. Así estaban 16 de los 18
// proveedores (STOCKSUR entre ellos), y la retención no practicada la responde
// el agente retenedor (art. 370 E.T.). Aquí se avisa y se ofrece la tarifa
// general de compras de Parámetros Fiscales.
export function avisoRetencionFicha(prov, sub) {
  if (!prov || !prov.id) return "";
  if ((Number(prov.retefuente) || 0) > 0) return "";
  if (prov.autorretenedor || prov.regimen_fiscal === "rst") return "";
  const pf = getParametrosFiscales() || {};
  const uvt = Number(pf.uvt) || 0;
  const baseUVT = Number(pf.retefuente_compras_base_uvt) || 0;
  const base = baseUVT * uvt;
  const tarifa = Number(pf.retefuente_compras_pct) || 0;
  if (!(sub > 0) || sub < base || tarifa <= 0) return "";
  return `
    <div data-fprov-aviso-rf style="margin-top:8px;background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:8px 10px;font-size:11px;color:#991b1b;">
      ⚠️ <b>No se le está reteniendo.</b> La compra (${fmtCOP(sub)}) supera la base de retención en la fuente
      (${baseUVT} UVT = ${fmtCOP(base)}), pero la ficha de ${escapeHTML(prov.nombre || "este proveedor")} no tiene tarifa.
      La retención no practicada la responde quien paga.
      <button data-action="fprov-aplicar-retefuente" data-id="${escapeHTML(prov.id)}"
        style="margin-left:6px;background:#b91c1c;color:white;border:none;padding:4px 10px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;">
        Aplicar ${String(tarifa).replace(".", ",")}% (compras) y guardarlo en la ficha</button>
      <div style="font-size:10px;color:#7f1d1d;margin-top:3px;">Si es un servicio, un autorretenedor o del régimen simple, corrige la ficha en Maestros → Proveedores en vez de usar este botón.</div>
    </div>`;
}

import { registerActions } from "../ui/actions.js";

registerActions({
  "fprov-crear-proveedor-xml": () => crearProveedorDesdeXML(),
  "fprov-aplicar-retefuente": (d) => {
    const tarifa = Number((getParametrosFiscales() || {}).retefuente_compras_pct) || 0;
    if (!tarifa) return;
    saveData("proveedores", (state.data.proveedores || []).map(p => p.id === d.id ? { ...p, retefuente: tarifa } : p));
    render();
  }
});
